import React, { useState, useCallback } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import ChartWidget from '../Widgets/ChartWidget';
import TableWidget from '../Widgets/TableWidget';
import MetricWidget from '../Widgets/MetricWidget';

const ResponsiveGridLayout = WidthProvider(Responsive);

const WidgetGrid = ({
  widgets = [],
  onWidgetUpdate,
  onWidgetDelete,
  isEditing = false,
  layout = [],
  onLayoutChange
}) => {
  const [currentBreakpoint, setCurrentBreakpoint] = useState('lg');

  const handleLayoutChange = useCallback((newLayout, layouts) => {
    onLayoutChange && onLayoutChange(newLayout, layouts);
  }, [onLayoutChange]);

  const handleBreakpointChange = useCallback((breakpoint) => {
    setCurrentBreakpoint(breakpoint);
  }, []);

  const handleWidgetEdit = useCallback((widgetId, updatedWidget) => {
    onWidgetUpdate && onWidgetUpdate(widgetId, updatedWidget);
  }, [onWidgetUpdate]);

  const handleWidgetRefresh = useCallback((widgetId) => {
    // Refresh logic can be handled here or passed down
    console.log('Refreshing widget:', widgetId);
  }, []);

  const renderWidget = (widget) => {
    const commonProps = {
      widget,
      onEdit: handleWidgetEdit,
      onRefresh: handleWidgetRefresh,
      isEditing,
      style: { overflow: 'hidden' }
    };

    switch (widget.type) {
      case 'chart':
        return <ChartWidget key={widget.id} {...commonProps} />;
      case 'table':
        return <TableWidget key={widget.id} {...commonProps} />;
      case 'metric':
        return <MetricWidget key={widget.id} {...commonProps} />;
      default:
        return (
          <div key={widget.id} style={{
            background: '#f5f5f5',
            border: '1px solid #d9d9d9',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '16px',
            color: '#999'
          }}>
            Unknown Widget Type: {widget.type}
          </div>
        );
    }
  };

  // Convert widgets to grid layout items
  const gridItems = widgets.map(widget => {
    const layoutItem = layout.find(l => l.i === widget.id) || {
      i: widget.id,
      x: 0,
      y: 0,
      w: 6,
      h: 4,
      minW: 3,
      minH: 2
    };

    return (
      <div key={widget.id} data-grid={layoutItem}>
        {renderWidget(widget)}
      </div>
    );
  });

  const layouts = {
    lg: layout.length > 0 ? layout : widgets.map((widget, index) => ({
      i: widget.id,
      x: (index % 2) * 6,
      y: Math.floor(index / 2) * 4,
      w: 6,
      h: 4,
      minW: 3,
      minH: 2
    })),
    md: layout.length > 0 ? layout.map(l => ({ ...l, w: Math.max(l.w * 0.8, 3) })) : [],
    sm: layout.length > 0 ? layout.map(l => ({ ...l, w: Math.max(l.w * 0.6, 2) })) : [],
    xs: layout.length > 0 ? layout.map(l => ({ ...l, w: Math.max(l.w * 0.4, 2) })) : []
  };

  return (
    <div style={{ width: '100%', height: '100%', padding: '16px' }}>
      <ResponsiveGridLayout
        className="layout"
        layouts={layouts}
        onLayoutChange={handleLayoutChange}
        onBreakpointChange={handleBreakpointChange}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }}
        cols={{ lg: 12, md: 10, sm: 6, xs: 4 }}
        rowHeight={100}
        isDraggable={isEditing}
        isResizable={isEditing}
        margin={[16, 16]}
        containerPadding={[0, 0]}
        useCSSTransforms={true}
        preventCollision={false}
        compactType="vertical"
      >
        {gridItems}
      </ResponsiveGridLayout>
    </div>
  );
};

export default WidgetGrid;